import { AsyncPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { GalleryDto } from '@picflow/types';
import { firstValueFrom } from 'rxjs';
import { AssetFeedback } from '../../../gallery-shared/interfaces/asset-feedback';
import { GROWTHBOOK } from '../../../growthbook';
import { DialogOptions } from '../../../interfaces/dialog-data.interface';
import { GalleryAccessLevel } from '../../../lib/access-level';
import { getAnonymousGuestName } from '../../../lib/get-anonymous-guest-name';
import { getFormControl } from '../../../lib/get-form-control';
import { AccessLevelService } from '../../../services/access-level.service';
import { OnGetData } from '../../../services/dialog.service';
import { GuestAuthService } from '../../../services/guest-auth.service';
import { GuestService } from '../../../services/guest.service';
import { MergeAccountService } from '../../../services/merge-account.service';
import { StateService } from '../../../services/state.service';
import { ToastService } from '../../../services/toast.service';
import { InputFieldComponent } from '../../../shared-extra/components/input-field/input-field.component';
import { AvatarPipe } from '../../../shared-extra/pipes/avatar.pipe';
import { AvatarComponent } from '../../../shared/components/avatar/avatar.component';
import { LoadingIndicatorComponent } from '../../../shared/components/loading-indicator/loading-indicator.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { AutofocusDirective } from '../../../shared/directives/autofocus.directive';
import { ButtonDirective } from '../../../shared/directives/button.directive';

@Component({
  selector: 'app-guest-profile-dialog',
  templateUrl: './guest-profile-dialog.component.html',
  styleUrls: ['./guest-profile-dialog.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    InputFieldComponent,
    ButtonDirective,
    LoadingIndicatorComponent,
    SvgComponent,
    AsyncPipe,
    AvatarPipe,
    AutofocusDirective,
    AvatarComponent,
  ],
})
export class GuestProfileDialogComponent implements OnGetData {
  dialogName = 'guest-profile';
  close: (...args) => void;
  saving: boolean;
  anonymousGuestName: string;
  gallery?: GalleryDto;
  accessLevel?: GalleryAccessLevel;
  feedback?: AssetFeedback;
  isAnonymous: boolean;

  form = new FormGroup({
    name: getFormControl('name'),
    email: getFormControl('emailOptional'),
  });

  constructor(
    public state: StateService,
    private guestService: GuestService,
    private toasts: ToastService,
    private guestAuth: GuestAuthService,
    private mergeAccountService: MergeAccountService,
    private accessLevelService: AccessLevelService
  ) {}

  public growthBook = inject(GROWTHBOOK);

  dialogOptions(): DialogOptions {
    return { size: 480, spacingX: 60, spacingY: 40 };
  }

  async onGetData(data: { gallery: GalleryDto; feedback: AssetFeedback }, close: (...args) => void) {
    this.close = close;
    this.gallery = data.gallery;
    this.feedback = data.feedback;
    await this.guestService.getGuest();

    if (!this.state.preGuestAnonymousId) this.guestAuth.prepAnonymousGuest();
    this.anonymousGuestName = getAnonymousGuestName(this.state.guest?.anonymous_id || this.state.preGuestAnonymousId);
    if (this.state.guestAuthToken && this.state.guest) {
      this.isAnonymous = this.state.guest.name === this.anonymousGuestName;
      if (!this.isAnonymous) {
        this.form.setValue({
          name: this.state.guest.name,
          email: this.state.guest.email,
        });
      }
    } else {
      this.isAnonymous = true;
      this.form.setValue({
        name: '',
        email: '',
      });
    }

    if (this.gallery && !this.accessLevel) {
      this.accessLevel = await this.accessLevelService.getAccessLevel(this.gallery, {
        ignoreMemberList: true,
        ignorePublicAccessLevel: true,
      });
    }
  }

  async saveExisting(): Promise<boolean> {
    const params = {
      name: this.form.value.name,
      email: this.form.value.email || undefined,
      anonymous_id: this.state.guest.anonymous_id,
    };
    if (this.state.guest.email?.toLowerCase() === params.email?.toLowerCase()) delete params.email;

    try {
      await this.guestService.updateGuest(params);
      return true;
    } catch (err) {
      if (!(err instanceof HttpErrorResponse)) throw err;
      if (err.status === 400 && err.error.code === 'account_already_exists') {
        // If the current guest already has an email then we can't merge
        if (this.state.guest.email) {
          this.toasts.add('Email already in use', 'error');
          return false;
        }

        // If the current guest has no email then we can merge the two accounts together
        const anonymousId = this.state.guest.anonymous_id;
        await this.guestAuth.authenticateWithEmail('Anonymous', params.email, true, this.gallery);
        await firstValueFrom(this.mergeAccountService.mergeGuestByAnonymousId(anonymousId));
        return true;
      }
    }
  }

  async saveNew(): Promise<boolean> {
    const value = this.form.value;

    if (value.email) {
      await this.guestAuth.authenticateWithEmail(
        value.name,
        value.email,
        true, // value.notifications
        this.gallery,
        this.feedback
      );
    } else {
      await this.guestAuth.authenticateAnonymously(this.gallery, value.name, this.feedback);
    }
    return true;
  }

  async save() {
    if (!this.form.valid) return;
    if (this.saving) return;
    this.saving = true;

    if (this.state.guestAuthToken) {
      const resp = await this.saveExisting();
      if (!resp) {
        this.saving = false;
        return;
      }
    } else {
      const resp = await this.saveNew();
      if (!resp) {
        this.saving = false;
        return;
      }
    }
    this.guestService.getGuest();

    this.saving = false;
    this.toasts.add('Profile updated', 'success');
    this.close(true);
  }

  logout() {
    this.guestAuth.logout();
    this.close();
  }

  devOnlySetDigestToInstantly() {
    this.guestService.updateGuest({ notification_settings: { digest: 'instantly' } });
  }
}
